import {process, State} from "@progress/kendo-data-query"
import {Button, Toolbar, ToolbarItem, ToolbarSpacer} from "@progress/kendo-react-buttons"
import {getter} from "@progress/kendo-react-common"
import {Field, Form, FormRenderProps} from "@progress/kendo-react-form"
import {
    getSelectedState,
    Grid,
    GridColumn,
    GridDataStateChangeEvent,
    GridHeaderSelectionChangeEvent,
    GridNoRecords,
    GridSelectionChangeEvent,
    GridToolbar,
} from "@progress/kendo-react-grid"
import {Input} from "@progress/kendo-react-inputs"
import {
    MDBBtn,
    MDBCard,
    MDBCol,
    MDBModal,
    MDBModalBody,
    MDBModalContent,
    MDBModalDialog,
    MDBModalHeader,
    MDBModalTitle,
    MDBRow,
} from "mdbreact"
import {useCallback, useEffect, useRef, useState} from "react"
import {useDispatch} from "react-redux"
import {
    addCustomPriceCustomers,
    fetchCustomPricingCustomers,
    fetchCustomPricingCustomersToAdd,
    removeCustomPriceCustomers,
    setCustomPriceCustomersToAdd,
    setSelectedCustomPricingId,
    showAddEditCustomersModal,
} from "../../containers/CustomPrices/customPricesSlice"
import {RootState} from "../../rootReducer"
import {LoadingPanel} from "../../shared/CommonItems"
import {useAppSelector} from "../../store"
import {hasPermission, PermissionsChecker} from "../Shared/permissionChecker"

const DATA_ITEM_KEY = "customerId"
const SELECTED_FIELD = "selected"
const customPricingEdit = "CustomPricing.Edit"
const idGetter = getter(DATA_ITEM_KEY)

const addInitialDataState: State = {skip: 0, take: 20}
const removeInitialDataState: State = {skip: 0, take: 20}

const AddEditCustomPriceCustomers = (): JSX.Element => {
    const dispatch = useDispatch()

    const formRef = useRef<any>(null)
    const showAddEditCustomers = useAppSelector(
        (state: RootState) => state.containers.customPrices.showAddEditCustomers,
    )
    const selectedRegionId = useAppSelector((state: RootState) => state.containers.customPrices.selectedRegionId)
    const addCustomersRecord = useAppSelector(
        (state: RootState) => state.containers.customPrices.customPriceCustomersToAdd,
    )
    const customers = useAppSelector((state: RootState) => state.containers.customPrices.customPriceCustomers)
    const selectedCustomPriceName = useAppSelector(
        (state: RootState) => state.containers.customPrices.selectedCustomPriceName,
    )

    const customPricingId = useAppSelector((state: RootState) => state.containers.customPrices.selectedCustomPricingId)
    const isModalLoading = useAppSelector((state: RootState) => state.containers.customPrices.isModalLoading)
    const addCustomerLoading = useAppSelector((state: RootState) => state.containers.customPrices.addCustomerLoader)
    const removeCustomerLoading = useAppSelector(
        (state: RootState) => state.containers.customPrices.removeCustomerLoader,
    )
    const assignedPermissions = useAppSelector((state: RootState) => state.application.permissions)
    const verifyPermission = (checkPermissions: string[]) => hasPermission(checkPermissions, assignedPermissions)

    const [addMockCustomers, setAddMockCustomers] = useState(addCustomersRecord)
    const [removeMockCustomers, setRemoveCustomers] = useState(customers)
    const [addDataState, setAddDataState] = useState<State>(addInitialDataState)
    const [removeDataState, setRemoveDataState] = useState<State>(removeInitialDataState)
    const [searchText, setSearchText] = useState<string>("")
    const [selectedState, setSelectedState] = useState<{
        [id: string]: boolean | number[]
    }>({})
    const [removeSelectedState, setRemoveSelectedState] = useState<{
        [id: string]: boolean | number[]
    }>({})

    useEffect(() => {
        if (!showAddEditCustomers) {
            dispatch(setCustomPriceCustomersToAdd([]))
            setSearchText("")
        }
    }, [showAddEditCustomers])

    useEffect(() => {
        if (customPricingId > 0) {
            dispatch(fetchCustomPricingCustomers(selectedRegionId, customPricingId))
        }
    }, [customPricingId, dispatch, selectedRegionId])

    useEffect(() => {
        setAddMockCustomers(addCustomersRecord.map(dataItem => ({...dataItem, selected: false})))
        setAddDataState(addInitialDataState)
    }, [addCustomersRecord])

    const onSelectionChange = useCallback(
        (event: GridSelectionChangeEvent) => {
            const newSelectedState = getSelectedState({
                event,
                selectedState,
                dataItemKey: DATA_ITEM_KEY,
            })
            setSelectedState(newSelectedState)
        },
        [selectedState],
    )

    const onHeaderSelectionChange = useCallback(
        (event: GridHeaderSelectionChangeEvent) => {
            const checkboxElement: any = event.syntheticEvent.target
            const checked = checkboxElement.checked
            const newSelectedState = {
                ...selectedState,
            }
            addMockCustomers.forEach(item => (newSelectedState[idGetter(item)] = checked))
            setSelectedState(newSelectedState)
        },
        [addMockCustomers, selectedState],
    )
    useEffect(() => {
        setRemoveCustomers(customers.map(dataItem => ({...dataItem, selected: false})))
        setRemoveDataState(removeInitialDataState)
    }, [customers])

    // save button click handler
    const saveButtonClick = () => {
        const selectedCustomers = Object.keys(selectedState).filter(key => selectedState[key])
        selectedCustomers.length > 0 &&
            dispatch(addCustomPriceCustomers(selectedRegionId, customPricingId, selectedCustomers, searchText))
        setSelectedState({})
    }

    const searchHandler = () => {
        const values = formRef.current.values
        setSearchText(values.customerSearch)
        values.customerSearch.trim().length >= 3 &&
            dispatch(fetchCustomPricingCustomersToAdd(selectedRegionId, customPricingId, values.customerSearch))
    }

    const removeButtonClick = () => {
        const selectedCustomers = Object.keys(removeSelectedState).filter(key => removeSelectedState[key])
        if (selectedCustomers.length > 0) {
            dispatch(removeCustomPriceCustomers(selectedRegionId, customPricingId, selectedCustomers, searchText))
        }
        setRemoveSelectedState({})
    }
    const onRemoveSelectionChange = useCallback(
        (event: GridSelectionChangeEvent) => {
            const newSelectedState = getSelectedState({
                event,
                selectedState: removeSelectedState,
                dataItemKey: DATA_ITEM_KEY,
            })
            setRemoveSelectedState(newSelectedState)
        },
        [removeSelectedState],
    )
    const onRemoveHeaderSelectionChange = useCallback(
        (event: GridHeaderSelectionChangeEvent) => {
            const checkboxElement: any = event.syntheticEvent.target
            const checked = checkboxElement.checked
            const newSelectedState = {
                ...removeSelectedState,
            }
            removeMockCustomers.forEach(item => (newSelectedState[idGetter(item)] = checked))
            setRemoveSelectedState(newSelectedState)
        },
        [removeMockCustomers, removeSelectedState],
    )

    const addCustomPricing = () => {
        return (
            <MDBCard>
                <MDBModalBody className="p-2" style={{height: " calc(100vh - 5rem)"}}>
                    {addCustomerLoading && <LoadingPanel />}
                    <Form
                        onSubmit={searchHandler}
                        initialValues={{customerSearch: searchText}}
                        key={JSON.stringify({customerSearch: searchText})}
                        ref={formRef}
                        ignoreModified={false}
                        render={(formRenderProps: FormRenderProps) => (
                            <>
                                <Grid
                                    style={{height: "calc(100vh - 6rem)"}}
                                    data={process(
                                        addMockCustomers.map(customer => ({
                                            ...customer,
                                            [SELECTED_FIELD]: selectedState[idGetter(customer)],
                                        })),
                                        addDataState,
                                    )}
                                    {...addDataState}
                                    pageable={{
                                        buttonCount: 2,
                                        pageSizes: false,
                                    }}
                                    sortable
                                    onDataStateChange={(e: GridDataStateChangeEvent) => setAddDataState(e.dataState)}
                                    dataItemKey={DATA_ITEM_KEY}
                                    selectedField={SELECTED_FIELD}
                                    selectable={{
                                        enabled: true,
                                        drag: false,
                                        cell: false,
                                        mode: "multiple",
                                    }}
                                    onSelectionChange={onSelectionChange}
                                    onHeaderSelectionChange={onHeaderSelectionChange}
                                >
                                    <GridToolbar>
                                        <div>
                                            <Field
                                                name="customerSearch"
                                                component={Input}
                                                placeholder={"Search"}
                                                required
                                            />
                                            <Button
                                                className="ms-2"
                                                type="submit"
                                                fillMode={"flat"}
                                                themeColor="primary"
                                                disabled={!formRenderProps.allowSubmit}
                                                onClick={formRenderProps.onSubmit}
                                            >
                                                Go
                                            </Button>
                                        </div>
                                        <ToolbarSpacer />
                                        {verifyPermission([customPricingEdit]) && (
                                            <Button
                                                // style={{cursor: "pointer", lineHeight: "1rem"}}
                                                fillMode="flat"
                                                themeColor={"primary"}
                                                iconClass="fa fa-save"
                                                title="Save"
                                                onClick={saveButtonClick}
                                                disabled={isModalLoading}
                                            >
                                                Add
                                            </Button>
                                        )}
                                    </GridToolbar>
                                    <GridNoRecords> no record found</GridNoRecords>
                                    <GridColumn
                                        field={SELECTED_FIELD}
                                        width={40}
                                        headerSelectionValue={
                                            addMockCustomers.length > 0 &&
                                            addMockCustomers.findIndex(item => !selectedState[idGetter(item)]) === -1
                                        }
                                    />
                                    <GridColumn field="accountNo" title="Account No" width={"110px"} />
                                    <GridColumn field="companyName" title="Customer" />
                                </Grid>
                            </>
                        )}
                    />
                </MDBModalBody>
            </MDBCard>
        )
    }

    const removeCustomPrice = () => {
        return (
            <MDBCard>
                <MDBModalBody style={{height: "calc(100vh - 5rem)"}} className="pt-2 ms-1 me-1">
                    {removeCustomerLoading && <LoadingPanel />}
                    <div className="row position-relative">
                        <Grid
                            style={{height: "calc(100vh - 6rem)"}}
                            className="ps-0 pe-0"
                            data={process(
                                removeMockCustomers.map(customer => ({
                                    ...customer,
                                    [SELECTED_FIELD]: removeSelectedState[idGetter(customer)],
                                })),
                                removeDataState,
                            )}
                            {...removeDataState}
                            pageable={{
                                buttonCount: 2,
                                pageSizes: false,
                            }}
                            sortable
                            onDataStateChange={(e: GridDataStateChangeEvent) => setRemoveDataState(e.dataState)}
                            dataItemKey={DATA_ITEM_KEY}
                            selectedField={SELECTED_FIELD}
                            selectable={{
                                enabled: true,
                                drag: false,
                                cell: false,
                                mode: "multiple",
                            }}
                            onSelectionChange={onRemoveSelectionChange}
                            onHeaderSelectionChange={onRemoveHeaderSelectionChange}
                        >
                            {verifyPermission([customPricingEdit]) && (
                                <GridToolbar>
                                    <Button
                                        fillMode="flat"
                                        themeColor={"primary"}
                                        iconClass="fa fa-trash"
                                        title="Remove"
                                        onClick={removeButtonClick}
                                    >
                                        Remove
                                    </Button>
                                </GridToolbar>
                            )}
                            {verifyPermission([customPricingEdit]) && (
                                <GridColumn
                                    field={SELECTED_FIELD}
                                    width={40}
                                    headerSelectionValue={
                                        customers.length > 0 &&
                                        customers.findIndex(item => !removeSelectedState[idGetter(item)]) === -1
                                    }
                                />
                            )}
                            <GridColumn field="accountNo" title="Account No" width={110} />
                            <GridColumn field="companyName" title="Customer" />
                        </Grid>
                    </div>
                    <div className="clear"></div>
                </MDBModalBody>
            </MDBCard>
        )
    }
    return (
        <MDBModal staticBackdrop animationDirection="right" show={showAddEditCustomers} tabIndex="-1">
            <MDBModalDialog position={`top right ${verifyPermission(["CustomPricing.Add"]) ? "modal-xl" : ""}`} side>
                <MDBModalContent className="full-height">
                    <MDBModalHeader>
                        <MDBModalTitle>{`Customers for ${selectedCustomPriceName}`}</MDBModalTitle>
                        <MDBBtn
                            color="none"
                            className="btn-close"
                            aria-label="Close"
                            onClick={() => {
                                dispatch(showAddEditCustomersModal(false))
                                dispatch(setSelectedCustomPricingId(0))
                            }}
                        ></MDBBtn>
                    </MDBModalHeader>
                    <MDBRow className="mt-1">
                        <PermissionsChecker permission={["CustomPricing.Add"]}>
                            <MDBCol md="6" className="ps-4 pe-0 pb-0">
                                {addCustomPricing()}
                            </MDBCol>
                        </PermissionsChecker>
                        <MDBCol
                            md={`${verifyPermission([customPricingEdit]) ? 6 : 12}`}
                            className={`${verifyPermission([customPricingEdit]) ? "pe-4" : "ps-4 pe-4"}`}
                        >
                            {removeCustomPrice()}
                        </MDBCol>
                    </MDBRow>
                </MDBModalContent>
            </MDBModalDialog>
        </MDBModal>
    )
}

export default AddEditCustomPriceCustomers
